"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Clock, Calendar, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
const formatExpDate = (dateString?: string) => {
  if (!dateString) return "EXP. --/--";
  const date = new Date(dateString);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `EXP. ${month}/${year}`;
};
const getReadingTime = (content?: string) => {
  if (!content) return "3 MIN READ";
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return `${minutes} MIN READ`;
};
function DarkroomInsightsConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col w-full">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="flex w-full gap-6 border-b border-border/40 py-8 animate-pulse">
            <div className="h-24 w-24 shrink-0 bg-muted/20" />
            <div className="flex flex-1 flex-col justify-center gap-3">
              <div className="h-8 w-3/4 bg-muted/20" />
              <div className="h-4 w-1/2 bg-muted/20" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="py-20 text-center">
        <p className="text-muted-foreground font-mono uppercase tracking-widest">
          Signal Lost. Unable to retrieve archives.
        </p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-20 text-center">
        <p className="text-muted-foreground font-mono uppercase tracking-widest">
          No records found in the darkroom.
        </p>
      </div>;
  }
  return <div className="w-full">
      <div className="flex flex-col border-t border-border/40">
        {posts.map((post, i) => <Link key={post.id} to={post.link || "#"} className="group relative flex flex-col sm:flex-row gap-6 border-b border-border/40 py-8 transition-colors duration-300 hover:bg-amber-950/20 px-4 sm:px-6" data-index={i}>
            {}
            <div className="shrink-0">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-24 w-24 object-cover grayscale transition-all duration-500 group-hover:grayscale-0 bg-muted" /> : <div className="flex h-24 w-24 items-center justify-center bg-muted/10 text-muted-foreground font-mono text-xs border border-border/20">
                  NO IMG
                </div>}
            </div>

            {}
            <div className="flex flex-1 flex-col justify-center">
              <h3 className="font-serif text-2xl sm:text-3xl font-light tracking-tight text-foreground transition-colors group-hover:text-primary">
                {post.title}
              </h3>
              
              <p className="mt-2 line-clamp-1 text-muted-foreground text-sm sm:text-base">
                {post.excerpt?.replace(/<[^>]*>?/gm, "") || "No summary available."}
              </p>

              {}
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-xs uppercase tracking-wider text-muted-foreground/70">
                <span className="flex items-center gap-1.5">
                  <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate>
                  {formatExpDate(post.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  {getReadingTime(post.content)}
                </span>
                <span className="ml-auto hidden sm:flex items-center gap-1 text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  Read Entry <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </div>
          </Link>)}
      </div>

      {}
      <div className="mt-12">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button variant="ghost" size="icon" onClick={() => prevPage()} disabled={!hasPrev} className="disabled:opacity-30">
                <span className="sr-only">Previous</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </Button>
            </PaginationItem>
            
            <PaginationItem className="hidden sm:block">
              <span className="flex h-9 items-center justify-center px-4 font-mono text-xs text-muted-foreground">
                PAGE {currentPage} / {totalPages || 1}
              </span>
            </PaginationItem>

            <PaginationItem>
              <Button variant="ghost" size="icon" onClick={() => nextPage()} disabled={!hasNext} className="disabled:opacity-30">
                <span className="sr-only">Next</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>;
}
export default function DarkroomInsights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="darkroom-insights" className="dark bg-background text-foreground py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        {}
        <div className="mb-16 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              Darkroom Insights
            </h2>
            <p className="mt-4 text-muted-foreground font-light leading-relaxed">
              Technical notes, exposure logs, and chemical recipes from the archives. 
              A collection of thoughts on the analog process.
            </p>
          </div>
          <div className="hidden md:block pb-2">
             <div className="h-px w-24 bg-primary/50" />
          </div>
        </div>

        {}
        <PostsProvider wp_query={wp_query}>
          <DarkroomInsightsConsumer />
        </PostsProvider>
      </div>
    </section>;
}